import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Label } from "../../atoms/label";
import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { PasswordField } from "../password-field";
import type { PasswordFieldProps } from "../password-field";
import { FormFieldMessage } from "./form-field-message";
import type { FormFieldBaseProps } from "./types";

export type FormPasswordFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldBaseProps<TFieldValues, TName> &
  Omit<PasswordFieldProps, "state" | "value" | "onChangeText" | "onBlur">;

export const FormPasswordField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  id: idProp,
  ...passwordFieldProps
}: FormPasswordFieldProps<TFieldValues, TName>) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { control } = useFormContext<TFieldValues>();

  return (
    <Box
      {...platformProps({ slot: "form-password-field", testID })}
      className={cn("gap-spacing-xxs flex flex-col", containerClassName)}
    >
      {label && (
        <Label
          htmlFor={id}
          required={required}
          disabled={passwordFieldProps.disabled}
          size={passwordFieldProps.size}
        >
          {label}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const displayError =
            fieldState.error && !hideErrorMessage
              ? (errorMessage ?? fieldState.error.message)
              : undefined;
          return (
            <>
              <PasswordField
                {...passwordFieldProps}
                ref={field.ref}
                // Native ignores the Label's htmlFor, so the input carries the label
                accessibilityLabel={passwordFieldProps.accessibilityLabel ?? label}
                id={id}
                testID={id}
                state={hasError ? "error" : "default"}
                value={field.value ?? ""}
                onChangeText={field.onChange}
                onBlur={field.onBlur}
              />
              <FormFieldMessage error={displayError} helperText={helperText} />
            </>
          );
        }}
      />
    </Box>
  );
};

FormPasswordField.displayName = "FormPasswordField";
